/**
 * Die Rechnung hinter der Großansicht — wie groß, wie weit, wie herum.
 *
 * **Sie steht für sich, damit sie zu messen ist.** In der Komponente verteilt
 * wäre sie nur über einen Browserlauf zu prüfen; als eigenes Modul bekommt sie
 * einen Lauf in Node (`tools/pruefstand/bildzoom.ts`) — dasselbe Vorgehen wie
 * bei der Drossel und den Farbketten.
 *
 * **Alle Maße sind Bildschirmpunkte**, keine Bildpunkte des Originals. Das ist
 * kein Detail: die Verschiebung geht als `translate(…px, …px)` **vor** das
 * `scale()` und wird damit nicht mitvergrößert. Wer hier in Originalpixeln
 * rechnet, bekommt einen Ausschnitt, der beim Vergrößern davonläuft.
 */

export interface Masse {
  breite: number
  hoehe: number
}

export interface Punkt {
  x: number
  y: number
}

/**
 * Wie weit ein Bild höchstens vergrößert wird.
 *
 * **Sechs, und die Zahl hat einen Grund.** Ein Panorama 5:1 auf einem
 * stehenden Telefon bräuchte das Zwölffache, um den Schirm zu decken — was
 * dann dasteht, ist ein Ausschnitt und kein Foto mehr. Bei den üblichen
 * Formaten (4:3, 3:2, 16:9) greift der Deckel nie: dort liegt der Faktor
 * zwischen zwei und vier.
 */
export const DECKEL = 6

/** Was zwei Finger höchstens hergeben — mehr ist Korn und kein Bild. */
export const HAND_DECKEL = 8

/**
 * Wie groß ein eingepasstes Bild wirklich dasteht.
 *
 * Eingepasst wird mit dem **kleineren** der beiden Verhältnisse; das ist die
 * Größe, gegen die alles andere hier gerechnet wird.
 *
 * **Und niemals größer als das Original** — die `1` in der Reihe. Das ist
 * nicht Geschmack, sondern die CSS-Regel nachgerechnet: `max-width: 100%`
 * verkleinert, es vergrößert nicht. Ohne die `1` stünde hier für ein kleines
 * Bild eine Größe, die auf dem Schirm nirgends zu sehen ist — und der Versatz
 * liefe beim Schieben ins Leere.
 */
export function gezeichnet(bild: Masse, fenster: Masse): Masse {
  if (bild.breite <= 0 || bild.hoehe <= 0) return { breite: 0, hoehe: 0 }
  const faktor = Math.min(fenster.breite / bild.breite, fenster.hoehe / bild.hoehe, 1)
  return { breite: bild.breite * faktor, hoehe: bild.hoehe * faktor }
}

/**
 * Der Faktor, der ein eingepasstes Bild den Schirm **füllen** lässt.
 *
 * Das ist die Zahl, um die es in #60 geht. Eingepasst wird mit dem kleineren
 * Verhältnis gerechnet, gefüllt mit dem größeren — der Quotient ist der Weg
 * von dem einen zum anderen. Ein Querformat 4:3 auf 390 × 844 Punkten kommt so
 * auf 2,9, und genau dann steht kein Grau mehr am Rand.
 *
 * **Eins für ein Bild, das schon passt.** Ein Hochformat auf einem stehenden
 * Telefon füllt den Schirm ohnehin fast; der Doppeltipp soll dort nicht aus
 * Prinzip etwas verändern.
 */
export function fuellFaktor(bild: Masse, fenster: Masse, deckel = DECKEL): number {
  if (bild.breite <= 0 || bild.hoehe <= 0 || fenster.breite <= 0 || fenster.hoehe <= 0) {
    return 1
  }
  // **Gerechnet wird gegen das, was dasteht**, nicht gegen das Original. Für
  // ein großes Foto ist das dasselbe; für ein kleines, das nicht vergrößert
  // dasteht, ist es der Unterschied zwischen „füllt" und „füllt eben nicht".
  const gross = gezeichnet(bild, fenster)
  if (gross.breite <= 0 || gross.hoehe <= 0) return 1
  const faktor = Math.max(fenster.breite / gross.breite, fenster.hoehe / gross.hoehe)
  return Math.min(Math.max(faktor, 1), deckel)
}

/**
 * Den Ausschnitt am Bild halten — **an keinem Rand darf Grau auftauchen.**
 *
 * Solange das vergrößerte Bild in einer Achse schmaler ist als das Fenster,
 * bleibt es dort mittig; verschieben ließe sich nur ins Leere.
 */
export function begrenze(versatz: Punkt, zoom: number, bild: Masse, fenster: Masse): Punkt {
  const gross = gezeichnet(bild, fenster)
  const weitX = Math.max(0, (gross.breite * zoom - fenster.breite) / 2)
  const weitY = Math.max(0, (gross.hoehe * zoom - fenster.hoehe) / 2)
  return {
    x: Math.min(Math.max(versatz.x, -weitX), weitX),
    y: Math.min(Math.max(versatz.y, -weitY), weitY),
  }
}

/**
 * Vergrößern **auf eine Stelle zu** und nicht auf die Mitte.
 *
 * Sonst springt weg, was man gerade ansehen wollte: wer unten rechts auf ein
 * Schild doppelt tippt, hat es nach dem Vergrößern außerhalb des Schirms.
 *
 * Die Rechnung: eine Bildstelle liegt bei `punkt = versatz + zoom · u`, wobei
 * `u` ihr Abstand von der Bildmitte im eingepassten Maß ist und `punkt` von
 * der **Fenstermitte** aus zählt. Soll sie unter dem Finger bleiben, folgt
 * daraus der neue Versatz von selbst.
 */
export function zoomeAuf(punkt: Punkt, versatz: Punkt, alt: number, neu: number): Punkt {
  if (alt <= 0) return versatz
  const anteil = neu / alt
  return {
    x: punkt.x - (punkt.x - versatz.x) * anteil,
    y: punkt.y - (punkt.y - versatz.y) * anteil,
  }
}

/** Der Abstand zwischen zwei Fingern — für das stufenlose Ziehen. */
export function abstand(a: Punkt, b: Punkt): number {
  return Math.hypot(a.x - b.x, a.y - b.y)
}
